import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw, RotateCcw, Settings2 } from 'lucide-react';

import { ApiError, useApi } from '../../lib/api';
import type {
    ApiResponse, Proposal, ProposalCapabilities, ProposalRunEvent,
} from '../../types';
import {
    Cost, Documents, FailedRun, initialRunOptions, RunOptionsFields, RunProgress,
    StatusBadge, Versions,
} from './parts';
import { ProposalChat } from './ProposalChat';
import { DemandeModifiable } from './DemandeModifiable';
import { JournalDesRuns } from './JournalDesRuns';

/**
 * Espace de travail d'une proposition : suivi, discussion, versions.
 *
 * La version dépliable de la modale imposait de choisir entre voir la liste et
 * voir une proposition. Ici la proposition courante est toujours entière, et la
 * discussion occupe la place qu'elle mérite : c'est par elle qu'on corrige, une
 * régénération complète n'étant plus que le dernier recours.
 */
export function ProposalWorkspace({ proposalId, caps }: {
    proposalId: string;
    caps?: ProposalCapabilities;
}) {
    const api = useApi();
    const queryClient = useQueryClient();
    const [events, setEvents] = useState<ProposalRunEvent[]>([]);
    const [showOptions, setShowOptions] = useState(false);
    const [options, setOptions] = useState(() => initialRunOptions(caps));

    useEffect(() => { setOptions(initialRunOptions(caps)); }, [caps]);

    const { data: detail } = useQuery({
        queryKey: ['proposal', proposalId],
        queryFn: () => api.get<ApiResponse<Proposal>>(`/api/proposals/${proposalId}`),
        // Une génération en cours change d'état sans action de l'utilisateur.
        refetchInterval: (query) => (
            (query.state.data as ApiResponse<Proposal> | undefined)?.data.status === 'generating'
                ? 2500 : false
        ),
    });

    const full = detail?.data;
    // La version la plus récente : c'est d'elle qu'on repart par défaut.
    const derniere = useMemo(
        () => (full?.versions || []).reduce<{ id: string; version: number } | undefined>(
            (haute, version) => (!haute || version.version > haute.version ? version : haute),
            undefined,
        ),
        [full?.versions],
    );

    const activeRun = useMemo(
        () => (full?.runs || []).find((run) => run.status === 'queued' || run.status === 'running'),
        [full?.runs],
    );

    /**
     * Lance une génération, en repartant d'une version ou de la consigne.
     *
     * Les deux intentions sont légitimes et ne se devinent pas. Reprendre une
     * version corrige sans défaire ce qui allait bien ; repartir de zéro sert
     * quand la structure elle-même est à revoir. L'écran ne choisissait pas :
     * il reprenait toujours la dernière version, donc recommencer était
     * impossible.
     */
    const regenerate = useMutation({
        mutationFn: (baseVersionId?: string) => api.post<ApiResponse<{ runId: string }>>(
            `/api/proposals/${proposalId}/generate`,
            {
                baseVersionId,
                language: options.language || undefined,
                templateId: options.templateId || undefined,
                model: options.model || undefined,
                effort: options.effort || undefined,
                tools: JSON.stringify(options.tools),
                webUnrestricted: options.webUnrestricted ? 'true' : 'false',
                webAllowlist: options.webUnrestricted ? undefined : options.webAllowlist || undefined,
                libraryIds: options.libraryIds.length > 0 ? options.libraryIds : undefined,
                sections: options.sections.length > 0 ? options.sections : undefined,
            },
        ),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['proposal', proposalId] });
            if (full) {
                void queryClient.invalidateQueries({
                    queryKey: ['proposals', full.opportunityId],
                });
            }
        },
    });

    if (!full) {
        return (
            <div className="card flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
            </div>
        );
    }

    return (
        <div className="space-y-4 min-w-0">
            <div className="card space-y-3">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <h2 className="font-semibold text-[var(--color-text)] truncate">
                            {full.title || 'Sans titre'}
                        </h2>
                        <div className="mt-0.5">
                            <DemandeModifiable proposal={full} />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {full.currentVersion > 0 && (
                            <span className="badge badge-default">v{full.currentVersion}</span>
                        )}
                        <StatusBadge status={full.status} />
                    </div>
                </div>
                <Cost runs={full.runs || []} />
            </div>

            {activeRun && (
                <RunProgress proposalId={proposalId} run={activeRun} onEvents={setEvents} />
            )}

            <ProposalChat
                proposalId={proposalId}
                activeRun={activeRun}
                events={activeRun ? events : []}
                currentVersion={full.currentVersion}
            />

            {!activeRun && (
                <div className="card space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <button
                            className="btn-secondary flex items-center gap-2 text-sm"
                            onClick={() => regenerate.mutate(derniere?.id)}
                            disabled={regenerate.isPending}
                            title={derniere
                                ? `Reprend la version ${derniere.version} et la corrige`
                                : 'Écrit la première version'}
                        >
                            {regenerate.isPending
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <RefreshCw className="w-4 h-4" />}
                            {derniere
                                ? `Reprendre la version ${derniere.version}`
                                : 'Générer la proposition'}
                        </button>
                        {derniere && (
                            <button
                                className="btn-secondary flex items-center gap-2 text-sm"
                                onClick={() => regenerate.mutate(undefined)}
                                disabled={regenerate.isPending}
                                title="Réécrit le plan entièrement depuis la consigne"
                            >
                                <RotateCcw className="w-4 h-4" />
                                Recommencer de zéro
                            </button>
                        )}
                        <button
                            className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)]
                                inline-flex items-center gap-1.5"
                            onClick={() => setShowOptions((open) => !open)}
                        >
                            <Settings2 className="w-4 h-4" />
                            {showOptions ? 'Masquer les options' : 'Options de génération'}
                        </button>
                    </div>
                    {showOptions && (
                        <RunOptionsFields caps={caps} value={options} onChange={setOptions} />
                    )}
                    {regenerate.isError && (
                        <p className="text-sm text-[var(--color-error)]">
                            {(regenerate.error as ApiError).message}
                        </p>
                    )}
                </div>
            )}

            <FailedRun runs={full.runs || []} />
            {/* Le journal des exécutions passées, réflexion du modèle comprise.
                Les événements étaient conservés sans que rien ne permette de les
                revoir. */}
            <JournalDesRuns proposalId={proposalId} runs={full.runs || []} />
            <Documents documents={full.documents || []} />
            <Versions
                proposal={full}
                sharepointConfigured={Boolean(caps?.sharepointConfigured)}
                onReprendre={activeRun ? undefined : (versionId) => regenerate.mutate(versionId)}
                occupe={regenerate.isPending}
            />
        </div>
    );
}
